// --- Enums ---

export type EstateType =
    | 'flat' | 'family_house' | 'land' | 'recreational'
    | 'garage' | 'apartment_house' | 'commercial';

export type OfferType = 'sale' | 'lease';

export type PriceUnit = 'total' | 'sqm';

export type LocationType = 'location' | 'cadastral_units';

export type EstateState =
    | 'new' | 'very_good' | 'after_reconstruction' | 'good'
    | 'bad' | 'project' | 'under_construction' | 'before_reconstruction' | 'to_demolition';

export type ConstructionType =
    | 'brick' | 'panel' | 'skeleton' | 'prefab' | 'wood'
    | 'timber' | 'stone' | 'mix';

export type BuildingType = 'single_story' | 'multi_story';

export type RoomLayout =
    | 'studio' | '1+1' | '1+kk' | '2+1' | '2+kk' | '3+1' | '3+kk'
    | '4+1' | '4+kk' | '5+1' | '5+kk' | '6+1' | '6+kk' | '7+1' | '7+kk'
    | '4+2' | '5+2' | '6+2' | '7+2' | 'atypical' | 'two_generation' | 'other';

export type Ownership = 'personal' | 'cooperative' | 'other';

export type CommercialType =
    | 'office' | 'store' | 'warehouse' | 'production' | 'restaurant'
    | 'accommodation' | 'agricultural' | 'wine_cellar' | 'attic_space' | 'other';

export type LandType =
    | 'building' | 'commercial' | 'recreational' | 'garden'
    | 'agronomic' | 'forest' | 'water' | 'others';

// --- Option types ---

export interface EnumOption<T extends string> {
    value: T;
    label: string;
}

/** The estate type tabs also carry the icon of the type (PHP is the single source of it). */
export interface EstateTypeOption extends EnumOption<EstateType> {
    icon: string;
}

// --- Location ---

export interface LocationValue {
    id: string;
    name: string;
}

// --- Config ---

export interface SearchFormConfig {
    estateTypes: EstateTypeOption[];
    offerTypes: Array<EnumOption<OfferType>>;
    estateStates: Array<EnumOption<EstateState>>;
    constructions: Array<EnumOption<ConstructionType>>;
    buildingTypes: Array<EnumOption<BuildingType>>;
    roomLayouts: Array<EnumOption<RoomLayout>>;
    ownerships: Array<EnumOption<Ownership>>;
    commercialTypes: Array<EnumOption<CommercialType>>;
    landTypes: Array<EnumOption<LandType>>;
    locationTypes: string[];
    submitUrl: string;
    initialValues?: Partial<SearchFormValues>;
    fixedOfferType?: OfferType;
    withoutDateOfSaleOnly?: boolean;
    showTabs?: boolean;
    monochromeTypeIcons?: boolean;
    cadastralUnitsLoadUrl: string;
    cadastralUnitsCenter: { latitude: number; longitude: number };
    afterSubmitUrl?: string;
    forceFavoriteOnly?: boolean;
    forceUserSourceOnly?: boolean;
    withPurchaseContract?: boolean;
    withNotActive?: boolean;
}

// --- Form values ---

export interface SearchFormValues {
    estateType: EstateType;
    offerType: OfferType;
    minDate: string;
    maxDate: string;
    minPrice: string;
    maxPrice: string;
    priceUnit: PriceUnit;
    locationType: LocationType;
    location: LocationValue | null;
    radius: string;
    cadastralUnits: string;
    minFloorArea: string;
    maxFloorArea: string;
    minBuiltUpArea: string;
    maxBuiltUpArea: string;
    minLandArea: string;
    maxLandArea: string;
    minFloor: string;
    maxFloor: string;
    floors: string;
    estateState: EstateState[];
    construction: ConstructionType[];
    buildingType: BuildingType[];
    roomLayout: RoomLayout[];
    ownership: Ownership[];
    commercialType: CommercialType[];
    landType: LandType[];
    description: string;
    purchaseContract: boolean;
    notActive: boolean;
    userSourceOnly: boolean;
    favoriteOnly: boolean;
    withoutDateOfSaleOnly: boolean;
}

// --- Field visibility ---

const FIELD_VISIBILITY: Record<string, EstateType[]> = {
    estateState: ['flat', 'family_house', 'garage', 'recreational', 'apartment_house', 'commercial'],
    construction: ['flat', 'family_house', 'garage', 'recreational', 'apartment_house', 'commercial'],
    floorArea: ['flat', 'family_house', 'garage', 'recreational', 'apartment_house', 'commercial'],
    builtUpArea: ['family_house', 'garage', 'recreational', 'apartment_house', 'commercial'],
    landArea: ['family_house', 'land', 'recreational'],
    floor: ['flat'],
    roomLayout: ['flat'],
    ownership: ['flat'],
    buildingType: ['family_house', 'recreational', 'apartment_house'],
    floors: ['family_house', 'recreational', 'apartment_house', 'commercial'],
    landType: ['land'],
    commercialType: ['commercial'],
};

export function isFieldVisible(fieldGroup: string, estateType: EstateType): boolean {
    const types = FIELD_VISIBILITY[fieldGroup];

    if (types === undefined) {
        return true;
    }

    return types.includes(estateType);
}

// --- Validation ---

export type ValidationErrors = Partial<Record<string, string>>;

function parseDateDMY(value: string): Date | null {
    const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

    if (!match) {
        return null;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const date = new Date(year, month, day);

    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        return null;
    }

    return date;
}

function parsePositiveFloat(value: string | number): number | null {
    if (typeof value === 'number') {
        return isNaN(value) || value < 0 ? null : value;
    }

    const cleaned = value.replace(/\s/g, '');
    if (cleaned === '') {
        return null;
    }

    const num = parseFloat(cleaned);

    if (isNaN(num) || num < 0) {
        return null;
    }

    return num;
}

function parseNonNegativeInt(value: string | number): number | null {
    if (typeof value === 'number') {
        return isNaN(value) || value < 0 || !Number.isInteger(value) ? null : value;
    }

    if (value === '') {
        return null;
    }

    const num = parseInt(value, 10);

    if (isNaN(num) || num < 0 || num.toString() !== value.trim()) {
        return null;
    }

    return num;
}

function parseInt_(value: string | number): number | null {
    if (typeof value === 'number') {
        return isNaN(value) || !Number.isInteger(value) ? null : value;
    }

    if (value === '') {
        return null;
    }

    const num = parseInt(value, 10);

    if (isNaN(num) || num.toString() !== value.trim()) {
        return null;
    }

    return num;
}

function validateNumericRange(
    minStr: string | number,
    maxStr: string | number,
    fieldLabel: string,
    errors: ValidationErrors,
    minKey: string,
    maxKey: string,
    parser: (v: string | number) => number | null,
    allowNegative: boolean = false,
): void {
    const min = minStr !== '' ? parser(minStr) : null;
    const max = maxStr !== '' ? parser(maxStr) : null;

    if (minStr !== '' && min === null) {
        errors[minKey] = `${fieldLabel} od: zadejte platné číslo`;
    } else if (!allowNegative && min !== null && min < 0) {
        errors[minKey] = `${fieldLabel} od: hodnota nesmí být záporná`;
    }

    if (maxStr !== '' && max === null) {
        errors[maxKey] = `${fieldLabel} do: zadejte platné číslo`;
    } else if (!allowNegative && max !== null && max < 0) {
        errors[maxKey] = `${fieldLabel} do: hodnota nesmí být záporná`;
    }

    if (min !== null && max !== null && min > max) {
        errors[minKey] = `${fieldLabel} od nesmí být větší než ${fieldLabel.toLowerCase()} do`;
    }
}

export function validate(values: SearchFormValues): ValidationErrors {
    const errors: ValidationErrors = {};

    // Date range - required
    const minDate = parseDateDMY(values.minDate);
    const maxDate = parseDateDMY(values.maxDate);

    if (!values.minDate) {
        errors.minDate = 'Zadejte začátek období';
    } else if (minDate === null) {
        errors.minDate = 'Neplatný formát data (DD.MM.YYYY)';
    }

    if (!values.maxDate) {
        errors.maxDate = 'Zadejte konec období';
    } else if (maxDate === null) {
        errors.maxDate = 'Neplatný formát data (DD.MM.YYYY)';
    }

    if (minDate !== null && maxDate !== null && minDate > maxDate) {
        errors.minDate = 'Období od nesmí být později než období do';
    }

    // Price range
    validateNumericRange(
        values.minPrice, values.maxPrice, 'Cena',
        errors, 'minPrice', 'maxPrice', parsePositiveFloat,
    );

    // Floor area range
    validateNumericRange(
        values.minFloorArea, values.maxFloorArea, 'Podlahová plocha',
        errors, 'minFloorArea', 'maxFloorArea', parseNonNegativeInt,
    );

    // Built-up area range
    validateNumericRange(
        values.minBuiltUpArea, values.maxBuiltUpArea, 'Zastavěná plocha',
        errors, 'minBuiltUpArea', 'maxBuiltUpArea', parseNonNegativeInt,
    );

    // Land area range
    validateNumericRange(
        values.minLandArea, values.maxLandArea, 'Plocha pozemku',
        errors, 'minLandArea', 'maxLandArea', parseNonNegativeInt,
    );

    // Floor range (allows negative for underground floors)
    validateNumericRange(
        values.minFloor, values.maxFloor, 'Podlaží',
        errors, 'minFloor', 'maxFloor', parseInt_, true,
    );

    // Floors (positive integer)
    if (values.floors !== '') {
        const f = parseNonNegativeInt(values.floors);

        if (f === null || f <= 0) {
            errors.floors = 'Zadejte kladné celé číslo';
        }
    }

    // Radius (positive float)
    if (values.radius !== '') {
        const r = parsePositiveFloat(values.radius);

        if (r === null) {
            errors.radius = 'Zadejte kladné číslo';
        }
    }

    return errors;
}

// --- Defaults ---

function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
}

export function createDefaultValues(config: SearchFormConfig): SearchFormValues {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());

    const defaults: SearchFormValues = {
        estateType: config.estateTypes.length > 0 ? config.estateTypes[0].value : 'flat',
        offerType: config.fixedOfferType ?? (config.offerTypes.length > 0 ? config.offerTypes[0].value : 'sale'),
        minDate: formatDate(sixMonthsAgo),
        maxDate: formatDate(now),
        minPrice: '',
        maxPrice: '',
        priceUnit: 'total',
        locationType: 'location',
        location: null,
        radius: '',
        cadastralUnits: '',
        minFloorArea: '',
        maxFloorArea: '',
        minBuiltUpArea: '',
        maxBuiltUpArea: '',
        minLandArea: '',
        maxLandArea: '',
        minFloor: '',
        maxFloor: '',
        floors: '',
        estateState: [],
        construction: [],
        buildingType: [],
        roomLayout: [],
        ownership: [],
        commercialType: [],
        landType: [],
        description: '',
        purchaseContract: false,
        notActive: false,
        userSourceOnly: config.forceUserSourceOnly ?? false,
        favoriteOnly: config.forceFavoriteOnly ?? false,
        withoutDateOfSaleOnly: config.withoutDateOfSaleOnly ?? false,
    };

    if (config.initialValues) {
        return {...defaults, ...config.initialValues};
    }

    return defaults;
}
